import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, MapPin } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

interface SettingData {
    id: number;
    company_name: string;
    address: string;
    email: string;
    contact_number: string;
    currency: string;
    logo: string | null;
}

export default function ViewGeneralSettings() {
    const [data, setData] = useState<SettingData[]>([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchData();
    }, [refresh]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response: any = await apiPost("settings/list", {});
            if (response && response.data) {
                setData(response.data);
            } else {
                setData(response);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load settings data");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_general-settings`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_general-settings`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`settings/delete/${id}`, {});
                    toast.success("Settings have been deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete settings", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = [
        {
            name: "Logo",
            cell: (row: SettingData) => (
                <div style={{ padding: '5px 0' }}>
                    {row.logo ? (
                        <img
                            src={`${mediaUrl}${row.logo}`}
                            alt="Logo"
                            style={{ maxWidth: '80px', maxHeight: '30px', objectFit: 'contain' }}
                        />
                    ) : (
                        <span className="text-muted small">No Logo</span>
                    )}
                </div>
            ),
        },
        {
            name: "Company Name",
            selector: (row: SettingData) => row.company_name,
            sortable: true,
            wrap: true,
        },
        {
            name: "Email",
            selector: (row: SettingData) => row.email,
            wrap: true,
        },
        {
            name: "Contact",
            selector: (row: SettingData) => row.contact_number,
            wrap: true,
        },
        {
            name: "Currency",
            selector: (row: SettingData) => row.currency,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row: SettingData) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('general-settings.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('general-settings.delete') && (
                        <Button size="sm" color="danger" onClick={() => row.id && handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs
                pageTitle="General Settings"
                parent="Master Settings"
                title="General Settings"
            />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            {/* If no permission to view at all, component won't even be reached securely due to routing, but just in case */}
                            {!hasPermission('general-settings.view') ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fa fa-lock fa-2x mb-2 d-block"></i>
                                    You do not have permission to view General Settings.
                                </div>
                            ) : (
                                <>
                                    <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-4">
                                        <H3 className="text-primary">System Settings Overview</H3>
                                        {/* Add button ONLY shows if no data exists and user has add permission */}
                                        {data.length === 0 && hasPermission('general-settings.add') && !loading && (
                                            <Btn color="primary" onClick={handleAdd}>
                                                <Plus size={16} className="me-2" /> Add Settings
                                            </Btn>
                                        )}
                                    </Col>

                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer />
        </div>
    );
}
